import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { logout, isAuthenticated, getStoredUser } from '../../services/authService';
import { ChevronLeft, ChevronRight, Home, BarChart3, PlusCircle, User, LogOut, KeyRound, Menu } from 'lucide-react';
import logo from '../../assets/Images/logo.png';
import './Sidenav.css';
import { ProfileCard } from '../../pages/profilecard/profileCard';

const Sidenav = () => {
  const navigate = useNavigate();

  // Initialize state from localStorage or default to closed for first-time visitors
  const getInitialSidebarState = () => {
    const stored = localStorage.getItem('sidebarOpen');
    if (stored !== null) {
      return stored === 'true';
    }
    return false; // Default to closed for first-time visitors
  };

  const [isOpen, setIsOpen] = useState(getInitialSidebarState);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check authentication status
    const checkAuth = () => {
      setAuthenticated(isAuthenticated());
      setUser(getStoredUser());
    };

    checkAuth();

    // Listen for storage changes (in case user logs in/out in another tab)
    window.addEventListener('storage', checkAuth);

    // Listen for custom authChange event (for same-tab login/logout)
    window.addEventListener('authChange', checkAuth);

    // Handle window resize - only auto-collapse on mobile, but respect user preference on desktop
    const handleResize = () => {
      const stored = localStorage.getItem('sidebarOpen');
      if (window.innerWidth <= 768) {
        // On mobile, default to closed
        setIsOpen(false);
      } else if (stored !== null) {
        // On desktop, restore user preference
        setIsOpen(stored === 'true');
      } else {
        // On desktop with no stored preference, default to open
        setIsOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('authChange', checkAuth);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setAuthenticated(false);
    setUser(null);
    navigate('/');
  };

  const toggleSidenav = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    // Persist sidebar state to localStorage
    localStorage.setItem('sidebarOpen', newState.toString());
  };

  return (
    <>
      {/* Floating menu button for mobile when sidebar is closed */}
      <button
        className={`mobile-menu-btn ${!isOpen ? 'visible' : ''}`}
        onClick={toggleSidenav}
        aria-label="Open menu"
      >
        <span className="menu-icon"><Menu size={24} /></span>
      </button>

      {/* Overlay for mobile - click to close sidebar */}
      {isOpen && window.innerWidth <= 768 && (
        <div className="sidenav-overlay" onClick={toggleSidenav}></div>
      )}

      <nav className={`sidenav ${isOpen ? 'open' : 'closed'}`}>
        <div className="sidenav-header">
          <Link className="sidenav-brand" to="/">
            <img src={logo} alt="Logo" className="sidenav-logo" />
          </Link>
          <button className="sidenav-toggle" onClick={toggleSidenav} aria-label="Toggle menu">
            {isOpen ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
          </button>
        </div>
        <ul className="sidenav-menu">
          <li className="sidenav-item">
            <Link className="sidenav-link" to="/">
              <span className="sidenav-icon"><Home size={20} /></span>
              <span className="sidenav-text">Home</span>
            </Link>
          </li>
          {authenticated ? (
            <>
              <li className="sidenav-item">
                <Link className="sidenav-link" to="/dashboard">
                  <span className="sidenav-icon"><BarChart3 size={20} /></span>
                  <span className="sidenav-text">Dashboard</span>
                </Link>
              </li>
              <li className="sidenav-item">
                <Link className="sidenav-link" to="/hotel-onboarding">
                  <span className="sidenav-icon"><PlusCircle size={20} /></span>
                  <span className="sidenav-text">Add Hotel</span>
                </Link>
              </li>
              <li className="sidenav-item sidenav-user">
                <Link className="sidenav-link" to="/profile">
                  <span className="sidenav-icon"><User size={20} /></span>
                  <span className="sidenav-text">{user?.name || user?.username}</span>
                </Link>
              </li>
              <li className="sidenav-item">
                <button className="sidenav-link sidenav-logout" onClick={handleLogout}>
                  <span className="sidenav-icon"><LogOut size={20} /></span>
                  <span className="sidenav-text">Logout</span>
                </button>
              </li>
            </>
          ) : (
            <li className="sidenav-item">
              <Link className="sidenav-link" to="/login">
                <span className="sidenav-icon"><KeyRound size={20} /></span>
                <span className="sidenav-text">Login</span>
              </Link>
            </li>
          )}
        </ul>
      </nav>
    </>
  );
};

export default Sidenav;
